import type { GraphModification, TemplateTask, Graph } from "@lr-notitia/notitia-designer/schemas";
import type { PluginCompilerResponse } from "@lr-notitia/notitia-model-store/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import { useGraphStore } from "../graph";

let graphStore: ReturnType<typeof useGraphStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	symbolsStore = useSymbolsStore();
}

export const templateTasks = (graph: Ref<Graph>) =>
	computed<TemplateTask[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "task" && node.layer === "template"
			) as TemplateTask[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function templateTask(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		add: (conceptId: string, dataobjectId: string) => add(domainId, conceptId, dataobjectId),
		update: (task: TemplateTask) => update(domainId, graph, task),
		remove: (id: string) => remove(domainId, graph, id),
		isDeactivated: (id: string) => isDeactivated(id, graph),
		toggleDeactivate: (id: string) => toggleDeactivate(domainId, id, graph),
	};
}

function byId(id: string, graph: Ref<Graph>): TemplateTask | undefined {
	let hit = graph.value.nodes[id];
	return hit && hit.type === "task" && hit.layer === "template" ? (hit as TemplateTask) : undefined;
}

function add(domainId: Ref<string>, conceptId: string, dataobjectId: string): Promise<GraphModification> {
	if (!graphStore) init();
	return new Promise((resolve, reject) => {
		let concept = graphStore.concept.task.byId(conceptId);
		if (!concept) return reject();
		api()
			.domain(domainId.value)
			.template()
			.tasks()
			.post({
				concept: concept,
				dataobjectId: dataobjectId,
			})
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	task: TemplateTask
): Promise<{ compilerResponse: PluginCompilerResponse; graphModification: GraphModification }> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[task.id]) return reject();
		api()
			.domain(domainId.value)
			.template()
			.task(task.id)
			.put(task)
			.then((d) => {
				handleGraphModification(d.graphModification);
				resolve(d);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.graphs()
			.patch({ [id]: null })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

function isDeactivated(id: string, graph: Ref<Graph>): boolean {
	const task = byId(id, graph);
	if (!task) return false;
	return task.context?.["nta-deactivated"] === true;
}
async function toggleDeactivate(domainId: Ref<string>, id: string, graph: Ref<Graph>): Promise<void> {
	const task = byId(id, graph);
	if (!task) return;
	if (!task.context) task.context = {};
	task.context["nta-deactivated"] = !task.context["nta-deactivated"];
	update(domainId, graph, task)
		.then(() => Promise.resolve())
		.catch(() => Promise.reject());
}
